/**
 * Client-side wrapper for the Setup wizard's AI calls.
 *
 * Replaces the prototype's `window.claude.complete(prompt)` — the calling
 * code (Steps 2/3/5/10) now imports `complete()` instead of touching `window`.
 *
 * For wizard steps, prefer `run()` together with a factory from `./prompts.ts`
 * — that keeps prompt definitions out of components.
 */
import type { AIRequest } from './prompts';

export async function complete(
  prompt: string,
  opts?: { studyContext?: string; maxTokens?: number },
): Promise<string> {
  const res = await fetch('/api/ai/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      studyContext: opts?.studyContext,
      maxTokens: opts?.maxTokens,
    }),
  });
  if (!res.ok) {
    const { error } = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(error || `AI request failed (${res.status})`);
  }
  const { text } = (await res.json()) as { text: string };
  return text;
}

/** Run an `AIRequest` produced by one of the factories in `./prompts.ts`. */
export function run(req: AIRequest): Promise<string> {
  return complete(req.prompt, {
    studyContext: req.studyContext,
    maxTokens: req.maxTokens,
  });
}

/** Best-effort JSON extraction. Wizard prompts ask for raw JSON, but
 *  models sometimes add stray prose — pull the first matching array/object. */
export function extractJSON<T = unknown>(text: string): T | null {
  const match = text.match(/(\[[\s\S]*\]|\{[\s\S]*\})/);
  if (!match) return null;
  try {
    return JSON.parse(match[1]) as T;
  } catch {
    return null;
  }
}
